import { Accordion, AccordionDetails, Box, Button, Card, CardContent, Dialog, DialogContent, FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import { NoDataCard } from 'components/card/NoDataCard'
import { ALIGN, CardTable, NewTable } from 'components/datatable/NewTable'
import { QualitometerFilterField, SimpleFilterField } from 'components/datatable/SimpleSelectionTable'
import Input from 'components/forms/Input'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import SimpleSelect from 'components/forms/SimpleSelect'
import SelectionTableModal from 'components/modal/SelectionTableModal'
import StepperDialog from 'components/modal/StepperDialog'
import { AccordionTitle } from 'components/styled/Accordions'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { difference, intersectionWith, isNil, uniq, keys, groupBy, includes, isUndefined, compact, isNaN } from 'lodash'
import PropTypes from 'prop-types'
import { QualityCampaignContext } from 'quality/components/campaign/QualityCampaign'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { nbPerPageLabel, nbPerPageLabelShort, nbPerPageLabelTiny } from 'referencial/constants/ReferencialConstants'
import { getParametersNature, getParametersType } from 'referencial/util/ReferencialUtils'
import { getStatusInformation } from 'referencial/util/StatusUtils'
import i18n from 'simple-react-i18n'
import { getLocalizationLabel, getLocalizationPicto } from 'utils/AnalyseUtils'
import { whiteColor } from 'utils/constants/ColorTheme'
import useBoolean from 'utils/customHook/useBoolean'
import useListGrouped from 'utils/customHook/useListGrouped'
import useListIndexed from 'utils/customHook/useListIndexed'
import useSandreList from 'utils/customHook/useSandreList'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import { authorizeContributors } from 'utils/HabilitationUtil'
import { hybridSearch } from 'utils/SearchUtil'
import { getI18nTitleDataLength, normalizeString } from 'utils/StringUtil'
import { SHOW_SANDRE_CODE, SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import Checkbox from 'components/forms/Checkbox'
import Select from 'components/forms/Select'

const EXCEPTION_MODE = {
    PARAMETERS: 'parameters',
    SELECTIONS: 'selections',
}

const DEFAULT_EXCEPTION = {
    id: undefined,
    planningId: undefined,
    stationsPlan: [],
    parametersToAddPlan: [],
    parametersToDeletePlan: [],
    selectionsToAddPlan: [],
    selectionsToDeletePlan: [],
}

const STEP = {
    STATION: 0,
    PARAMETER: 1,
    EXCEPTION: 2,
}

const StepSelectStations = ({
    planning = {},
    setPlanning = () => {},
    campaignStations = [],
}) => {
    const { campaign } = useContext(QualityCampaignContext)
    const {
        qualitometers,
        allPoints,
        cities,
        accountUser,
        contributors,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        allPoints: store.QualityReducer.allPoints,
        cities: store.CityReducer.cities,
        accountUser: store.AccountReducer.accountUser,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)
    const indexedCities = useListIndexed(cities, 'id')
    const indexedQualitometers = useListIndexed(qualitometers, 'id')
    const groupedSamplePoints = useListGrouped(allPoints, 'idQualitometer')
    const codificationsList = useSandreList('QUALITOMETRES.NOMENCLATURES')

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)
    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [filter, setFilter] = useState({})

    const formattedStations = campaignStations.map(cs => {
        const qualito = indexedQualitometers[cs.stationId]
        const isSelected = planning.stationsPlan.includes(cs.id)
        const city = indexedCities[qualito?.townCode]
        const cityLabel = (showInseeCode ? city?.labelWithCode : city?.name) ?? ''
        const samplePoint = groupedSamplePoints[cs.stationId]?.find(p => p.idPoint === cs.samplePoint)
        return {
            code: qualito?.code,
            name: qualito?.name,
            city: cityLabel,
            codification: codificationsList.find(c => c.code === cs.codification)?.name,
            samplePoint: showSandreCode ? samplePoint?.displayLabel : samplePoint?.name,
            csId: cs.id,
            isSelected,
            _normalizedSearchString: [qualito?.code, qualito?.name, cityLabel].map(s => normalizeString(s)),
        }
    })

    const filteredStations = useMemo(() => {
        const {
            filter: filterId = -1,
            resultFilter,
            searchValue,
            city,
        } = filter

        const filterFilter = filterId !== -1 ? intersectionWith(formattedStations, resultFilter, (elem, res) => elem.id === res.id) : formattedStations
        const filterCity = !isNil(city) ? filterFilter.filter(s => s.townCode === city) : filterFilter
        return hybridSearch(filterCity, normalizeString(searchValue))
    }, [formattedStations, filter])


    const actions = filteredStations.every(s => s.isSelected) ? [{
        onClick: () => setPlanning(p => ({ ...p, stationsPlan: difference(p.stationsPlan, filteredStations.map(s => s.csId)) })),
        icon: 'check_box',
        tooltip: i18n.unselectAll,
    }] : [{
        onClick: () => setPlanning(p => ({ ...p, stationsPlan: uniq([...p.stationsPlan, ...filteredStations.map(s => s.csId)]) })),
        icon: 'check_box_outline_blank',
        tooltip: i18n.selectAll,
    }]

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={2}>
                            <SimpleDatePicker
                                id='startDate'
                                label={i18n.startDate}
                                value={planning.startDate}
                                onChange={startDate => setPlanning(p => ({ ...p, startDate: isNil(startDate) || isNaN(startDate) ? undefined : startDate }))}
                                obligatory
                            />
                        </Grid>
                        <Grid size={2}>
                            <SimpleDatePicker
                                id='endDate'
                                label={i18n.endDate}
                                value={planning.endDate}
                                onChange={endDate => setPlanning(p => ({ ...p, endDate: isNil(endDate) || isNaN(endDate) ? undefined : endDate }))}
                                min={planning.startDate}
                            />
                        </Grid>
                        <Grid size={'grow'}>
                            <Select
                                label={i18n.completion}
                                options={authorizeContributors(contributors, accountUser, planning.producer)
                                    .filter(({ id }) => includes(uniq([campaign.recipientCode, campaign.samplerCode, campaign.contributorCode, ...(campaign.secondaryLabos || [])]).map(code => parseInt(code)), id))
                                    // la valeur enregistrée est l'identifiant de l'intervenant, le mnémonique n'est qu'affiché
                                    .map(c => ({ ...c, name: c.mnemonique ?? c.name }))}
                                value={planning.producer}
                                onChange={producer => setPlanning(p => ({ ...p, producer }))}
                                keyValue='id'
                                integerValue
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Card>
                <CardContent>
                    <QualitometerFilterField
                        filter={filter}
                        setFilter={setFilter}
                    />
                </CardContent>
            </Card>
            <CardTable
                title={i18n.qualitometers}
                actions={actions}
                headers={['code', 'name', 'city', 'codification', 'samplePoint']}
                rows={filteredStations}
                onClickRow={({ isSelected, csId }) => {
                    setPlanning(p => {
                        const stationsPlan = isSelected ? p.stationsPlan.filter((id) => id !== csId) : [...p.stationsPlan, csId]
                        return { ...p, stationsPlan }
                    })
                }}
                lineActions={[{
                    icon: ({ isSelected }) => isSelected ? 'check_box' : 'check_box_outline_blank',
                    align: ALIGN.RIGHT,
                    onClick: ({ isSelected, csId }) => {
                        setPlanning(p => {
                            const stationsPlan = isSelected ? p.stationsPlan.filter((id) => id !== csId) : [...p.stationsPlan, csId]
                            return { ...p, stationsPlan }
                        })
                    },
                }]}
            />
        </Stack>
    )
}

StepSelectStations.propTypes = {
    planning: PropTypes.shape({}),
    setPlanning: PropTypes.func,
    campaignStations: PropTypes.arrayOf(PropTypes.shape({})),
}

const StepSelectParameters = ({
    planning = {},
    setPlanning = () => {},
    campaignParameters = [],
}) => {
    const {
        parameters,
        fractions,
        supports,
        units,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        units: store.UnitReducer.units,
    }), shallowEqual)
    const indexedParameters = useListIndexed(parameters, 'code')
    const indexedUnits = useListIndexed(units, 'code')
    const indexedSupports = useListIndexed(supports, 'code')
    const indexedFractions = useListIndexed(fractions, 'code')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    // const analysePlace = useSandreList('ANALYSES.LIEU')
    const types = getParametersType()
    const natures = getParametersNature()

    const [searchValue, setSearchValue] = useState()

    const formattedParameters = campaignParameters.map((cp) => {
        const { parameterCode, fractionCode, supportCode, placeCode, unitCode, selectionCode } = cp
        const isSelected = selectionCode ? includes(planning.selections, selectionCode) : planning.parametersPlan.includes(cp.id)
        const parameter = indexedParameters[parameterCode]
        return {
            name: parameter?.name.length > 60 ? parameter?.shortLabel : parameter?.name,
            type: types[parameter.typeParam] || '',
            nature: natures[parameter.nature] || '',
            status: parameter.status ? getStatusInformation(parameter.status) : i18n.validated,
            unit: showSandreCode ? indexedUnits[unitCode]?.symbolWithCode : indexedUnits[unitCode]?.symbol,
            fraction: showSandreCode ? indexedFractions[fractionCode]?.labelWithCode : indexedFractions[fractionCode]?.name,
            support: showSandreCode ? indexedSupports[supportCode]?.labelWithCode : indexedSupports[supportCode]?.name,
            place: getLocalizationPicto(placeCode),
            code: parameterCode,
            cpId: cp.id,
            isSelected,
            selectionCode,
            _normalizedSearchString: [parameter?.name, parameter?.shortLabel, parameterCode, indexedUnits[unitCode]?.symbolWithCode, indexedFractions[fractionCode]?.labelWithCode, indexedSupports[supportCode]?.labelWithCode, getLocalizationLabel(placeCode)].map(s => normalizeString(s)),
        }
    })

    const filteredParameters = useMemo(() => {
        return hybridSearch(formattedParameters, normalizeString(searchValue))
    }, [searchValue, formattedParameters])

    const groupedParametersBySelection = useMemo(() => groupBy(filteredParameters, 'selectionCode'), [filteredParameters])

    const formattedSelectionsWithParameters = useMemo(() => (
        keys(groupedParametersBySelection).map(selection => ({
            name: selection !== 'undefined' ? selection : i18n.outOfSelections,
            parameters: groupedParametersBySelection[selection],
        }))
    ), [groupedParametersBySelection])

    const selectionCodes = keys(groupedParametersBySelection).filter(s => s !== 'undefined')

    const isAllSelected = selectionCodes.length > 0 && selectionCodes.every(s => includes(planning.selections, s))

    const toggleAll = () => {
        const cpIds = filteredParameters.filter(({ selectionCode }) => selectionCode).map(({ cpId }) => cpId)
        setPlanning(p => isAllSelected ? ({
            ...p,
            selections: p.selections.filter(s => !includes(selectionCodes, s)),
            parametersPlan: difference(p.parametersPlan, cpIds),
        }) : ({
            ...p,
            selections: uniq([...p.selections, ...selectionCodes]),
            parametersPlan: uniq([...p.parametersPlan, ...cpIds]),
        }))
    }

    const actions = (selection) => {
        if (selection) {
            return includes(planning.selections, selection) ? [{
                onClick: () => {
                    setPlanning(p => ({
                        ...p,
                        selections: p.selections.filter(s => s !== selection),
                        parametersPlan: [
                            ...filteredParameters.filter(({ selectionCode, isSelected }) => (
                                isUndefined(selectionCode) ? isSelected : selectionCode !== selection && isSelected
                            )).map(s => s.cpId),
                        ],
                    }))
                },
                icon: 'check_box',
                tooltip: i18n.unselectAll,
                color: whiteColor,
            }] : [{
                onClick: () => {
                    setPlanning(p => ({
                        ...p,
                        selections: uniq([...p.selections, selection]),
                        parametersPlan: [
                            ...filteredParameters.filter(({ selectionCode, isSelected }) => (
                                isUndefined(selectionCode) ? isSelected : selectionCode !== selection && isSelected
                            )).map(s => s.cpId),
                            ...filteredParameters.filter(({ selectionCode }) => selectionCode === selection).map(s => s.cpId),
                        ],
                    }))
                },
                icon: 'check_box_outline_blank',
                tooltip: i18n.selectAll,
                color: whiteColor,
            }]
        }
        return filteredParameters.filter(({ selectionCode }) => !selectionCode).every(param => param.isSelected) ?
            [{
                onClick: () => {
                    setPlanning(p => ({
                        ...p,
                        parametersPlan: difference(
                            p.parametersPlan,
                            filteredParameters.filter(({ selectionCode }) => !selectionCode).map(s => s.cpId),
                        ),
                    }))
                },
                icon: 'check_box',
                tooltip: i18n.unselectAll,
                color: whiteColor,
            }] : [{
                onClick: () => {
                    setPlanning(p => ({
                        ...p,
                        parametersPlan: uniq([
                            ...p.parametersPlan,
                            ...filteredParameters.filter(({ selectionCode }) => !selectionCode).map(s => s.cpId),
                        ]),
                    }))
                },
                icon: 'check_box_outline_blank',
                tooltip: i18n.selectAll,
                color: whiteColor,
            }]
    }

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Grid container>
                        <Grid size={6}>
                            <Input
                                title={i18n.search}
                                value={searchValue}
                                onChange={setSearchValue}
                            />
                        </Grid>
                        <Grid
                            container
                            size={6}
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'end',
                                paddingRight: '3rem',
                            }}
                        >
                            <Checkbox
                                checked={isAllSelected}
                                onChange={() => toggleAll()}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {formattedSelectionsWithParameters.map((selection, i) => {
                return (
                    <Accordion key={i} defaultExpanded={selection.name === i18n.outOfSelections}>
                        <AccordionTitle
                            title={`${selection.name} (${selection?.parameters?.length || 0} ${getI18nTitleDataLength(i18n.parameter, i18n.parameters, selection?.parameters?.length || 0)})`}
                            actions={actions(selection.name !== i18n.outOfSelections ? selection.name : undefined)}
                        />
                        <NewTable
                            headers={['name', 'type', 'nature', 'status', 'unit', 'fraction', 'support', 'place']}
                            rows={selection.parameters}
                            onClickRow={({ isSelected, cpId }) => {
                                if (selection.name === i18n.outOfSelections) {
                                    setPlanning(p => ({
                                        ...p,
                                        parametersPlan: isSelected ? p.parametersPlan.filter(id => id !== cpId) : [...p.parametersPlan, cpId],
                                    }))
                                }
                            }}
                            lineActions={[{
                                icon: ({ isSelected }) => isSelected ? 'check_box' : 'check_box_outline_blank',
                                align: ALIGN.RIGHT,
                                onClick: ({ isSelected, cpId }) => {
                                    if (selection.name === i18n.outOfSelections) {
                                        setPlanning(p => ({
                                            ...p,
                                            parametersPlan: isSelected ? p.parametersPlan.filter(id => id !== cpId) : [...p.parametersPlan, cpId],
                                        }))
                                    }
                                },
                            }]}
                            maxHeight='45vh'
                            rowsPerPageOptions={nbPerPageLabel}
                        />
                    </Accordion>
                )
            })}
        </Stack>
    )
}

StepSelectParameters.propTypes = {
    planning: PropTypes.shape({}),
    setPlanning: PropTypes.func,
    campaignParameters: PropTypes.arrayOf(PropTypes.shape({})),
}

const AccordionException = ({
    exception,
    index,
    setPlanningExceptions,
    formattedStations,
    formattedParameters,
    formattedSelections,
    planningParameters,
    planningSelections,
    campaignParameters,
}) => {
    const [exceptionMode, setExceptionMode] = useState(exception.selectionsToAddPlan.length || exception.selectionsToDeletePlan.length ? EXCEPTION_MODE.SELECTIONS : EXCEPTION_MODE.PARAMETERS)
    const stationsPopin = useBoolean(false)
    const parametersPopin = useBoolean(false)

    const isParamMode = exceptionMode === EXCEPTION_MODE.PARAMETERS

    const paramsOfSelections = codes => codes.reduce((params, selCode) => (
        [
            ...params,
            ...campaignParameters.filter(({ selectionCode }) => selectionCode === selCode).map(({ id }) => id),
        ]
    ), [])

    useUpdateEffect(() => {
        setPlanningExceptions(prev =>
            prev.map((pE, i) =>
                i === index ?
                    {
                        ...pE,
                        parametersToAddPlan: [],
                        parametersToDeletePlan: isParamMode ? [...planningParameters] : paramsOfSelections(planningSelections),
                        selectionsToAddPlan: [],
                        selectionsToDeletePlan: isParamMode ? [] : [...planningSelections],
                    }
                    :
                    pE,
            ),
        )
    }, [exceptionMode])

    const analyzedParamIds = [
        ...planningParameters.filter(id => !includes(exception.parametersToDeletePlan, id)),
        ...exception.parametersToAddPlan,
    ]
    const analyzedSelectionCodes = [
        ...planningSelections.filter(code => !includes(exception.selectionsToDeletePlan, code)),
        ...exception.selectionsToAddPlan,
    ]

    const stationRows = formattedStations.filter(({ id }) => includes(exception.stationsPlan, id))
    const paramRows = formattedParameters.filter(({ id }) => includes(analyzedParamIds, id))
    const selectionRows = formattedSelections.filter(({ id }) => includes(analyzedSelectionCodes, id))

    const onValidateStations = list => {
        setPlanningExceptions(prev => prev.map((pE, i) => i === index ? { ...pE, stationsPlan: list } : pE))
        stationsPopin.setFalse()
    }
    const onValidateParameters = list => {
        setPlanningExceptions(prev => prev.map((pE, i) => i === index ? {
            ...pE,
            parametersToAddPlan: list.filter(id => !includes(planningParameters, id)),
            parametersToDeletePlan: planningParameters.filter(id => !includes(list, id)),
        } : pE))
        parametersPopin.setFalse()
    }
    const onValidateSelections = list => {
        const selectionsToAddPlan = list.filter(code => !includes(planningSelections, code))
        const selectionsToDeletePlan = planningSelections.filter(code => !includes(list, code))
        setPlanningExceptions(prev => prev.map((pE, i) => i === index ? {
            ...pE,
            selectionsToAddPlan,
            selectionsToDeletePlan,
            parametersToAddPlan: paramsOfSelections(selectionsToAddPlan),
            parametersToDeletePlan: paramsOfSelections(selectionsToDeletePlan),
        } : pE))
        parametersPopin.setFalse()
    }

    return (
        <Accordion key={index} defaultExpanded>
            <AccordionTitle
                title={`${i18n.exception} n°${index+1}`}
                actions={[
                    {
                        icon: 'close',
                        onClick: () => setPlanningExceptions(prev => prev.toSpliced(index, 1)),
                        color: whiteColor,
                    },
                ]}
            />
            <AccordionDetails>
                <Stack spacing={1}>
                    <RadioGroup row value={exceptionMode} onChange={e => setExceptionMode(e.target.value)}>
                        <FormControlLabel value={EXCEPTION_MODE.PARAMETERS} control={<Radio />} label={i18n.parameters} />
                        <FormControlLabel value={EXCEPTION_MODE.SELECTIONS} control={<Radio />} label={i18n.selections} />
                    </RadioGroup>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <CardTable
                                title={i18n.selectedStations}
                                rows={stationRows}
                                headers={['code', { key: 'city', width: '150px' }, { key: 'name', width: '150px' }, 'codification', 'samplePoint']}
                                rowsPerPageOptions={nbPerPageLabelTiny}
                                actions={[{ icon: 'compare_arrows', tooltip: i18n.edit, onClick: stationsPopin.setTrue }]}
                            />
                        </Grid>
                        <Grid size={6}>
                            {isParamMode ? (
                                <CardTable
                                    title={i18n.analyzedParameters}
                                    rows={paramRows}
                                    headers={['code', { key: 'name', width: '100px' }, 'unit', 'fraction', 'support', 'place', 'selectionCode']}
                                    rowsPerPageOptions={nbPerPageLabelTiny}
                                    actions={[{ icon: 'compare_arrows', tooltip: i18n.edit, onClick: parametersPopin.setTrue }]}
                                />
                            ) : (
                                <CardTable
                                    title={i18n.analyzedSelections}
                                    rows={selectionRows}
                                    headers={['code', 'name', 'nbParameters']}
                                    rowsPerPageOptions={nbPerPageLabelTiny}
                                    actions={[{ icon: 'compare_arrows', tooltip: i18n.edit, onClick: parametersPopin.setTrue }]}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Stack>
            </AccordionDetails>
            <SelectionTableModal
                isOpen={stationsPopin.value}
                onClose={stationsPopin.setFalse}
                onValidate={onValidateStations}
                title={i18n.stations}
                dialogHeight='auto'
                listData={formattedStations}
                defaultSelectionList={exception.stationsPlan}
                listHeaders={['code', { key: 'city', width: '150px' }, { key: 'name', width: '150px' }, 'codification', 'samplePoint']}
                listTitle={i18n.nonSelectedStations}
                selectionListTitle={i18n.selectedStations}
                filterField={SimpleFilterField}
                filterFunction={(list, { searchValue }) => hybridSearch(list, normalizeString(searchValue))}
                nbPerPage={nbPerPageLabelShort}
            />
            {isParamMode ? (
                <SelectionTableModal
                    isOpen={parametersPopin.value}
                    onClose={parametersPopin.setFalse}
                    onValidate={onValidateParameters}
                    title={i18n.parameters}
                    dialogHeight='auto'
                    listData={formattedParameters}
                    defaultSelectionList={analyzedParamIds}
                    listHeaders={['code', { key: 'name', width: '100px' }, 'unit', 'fraction', 'support', 'place', 'selectionCode']}
                    listTitle={i18n.nonSelectedParameters}
                    selectionListTitle={i18n.selectedParameters}
                    filterField={SimpleFilterField}
                    filterFunction={(list, { searchValue }) => hybridSearch(list, normalizeString(searchValue))}
                    nbPerPage={nbPerPageLabelShort}
                />
            ) : (
                <SelectionTableModal
                    isOpen={parametersPopin.value}
                    onClose={parametersPopin.setFalse}
                    onValidate={onValidateSelections}
                    title={i18n.selections}
                    dialogHeight='auto'
                    listData={formattedSelections}
                    defaultSelectionList={analyzedSelectionCodes}
                    listHeaders={['code', 'name', 'nbParameters']}
                    listTitle={i18n.nonSelectedSelections}
                    selectionListTitle={i18n.selectedSelections}
                    filterField={SimpleFilterField}
                    filterFunction={(list, { searchValue }) => hybridSearch(list, normalizeString(searchValue))}
                    nbPerPage={nbPerPageLabelShort}
                />
            )}
        </Accordion>
    )
}

AccordionException.propTypes = {
    exception: PropTypes.shape({
        stationsPlan: PropTypes.arrayOf(PropTypes.number),
        parametersToDeletePlan: PropTypes.arrayOf(PropTypes.number),
        parametersToAddPlan: PropTypes.arrayOf(PropTypes.number),
        selectionsToDeletePlan: PropTypes.arrayOf(PropTypes.string),
        selectionsToAddPlan: PropTypes.arrayOf(PropTypes.string),
    }),
    index: PropTypes.number,
    setPlanningExceptions: PropTypes.func,
    formattedStations: PropTypes.arrayOf(PropTypes.shape({})),
    formattedParameters: PropTypes.arrayOf(PropTypes.shape({})),
    formattedSelections: PropTypes.arrayOf(PropTypes.shape({})),
    planningParameters: PropTypes.arrayOf(PropTypes.shape({})),
    planningSelections: PropTypes.arrayOf(PropTypes.shape({})),
    campaignParameters: PropTypes.arrayOf(PropTypes.shape({})),
}

const StepSelectExceptions = ({
    planningId,
    planningStations,
    planningParameters,
    planningSelections = [],
    planningExceptions,
    setPlanningExceptions,
    campaignStations,
    campaignParameters,
    campaignSelections = [],
}) => {
    const {
        citiesIndex,
        qualitosIndex,
        parametersIndex,
        units,
        fractions,
        supports,
        allPoints,
        selections,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
        qualitosIndex: store.QualityReducer.qualitosIndex,
        parametersIndex: store.ParameterReducer.parametersIndex,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        allPoints: store.QualityReducer.allPoints,
        selections: store.ParameterReducer.selectionsWithParam,
    }), shallowEqual)

    const indexedSelections = useListIndexed(selections, 'code')
    const indexedUnits = useListIndexed(units, 'code')
    const indexedFractions = useListIndexed(fractions, 'code')
    const indexedSupports = useListIndexed(supports, 'code')
    const groupedSamplePoints = useListGrouped(allPoints, 'idQualitometer')
    const codificationsList = useSandreList('QUALITOMETRES.NOMENCLATURES')

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)
    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const formattedStations = campaignStations.filter(({ id }) => includes(planningStations, id)).map(station => {
        const qualitometer = qualitosIndex[station.stationId]
        const city = citiesIndex[qualitometer?.townCode]
        const cityLabel = (showInseeCode ? city?.labelWithCode : city?.name) ?? ''
        const samplePoint = groupedSamplePoints[station.stationId]?.find(p => p.idPoint === station.samplePoint)
        return {
            id: station.id,
            code: qualitometer?.code,
            name: { value: qualitometer?.name, tooltip: qualitometer?.name },
            city: { value: cityLabel, tooltip: cityLabel },
            codification: codificationsList.find(c => c.code === station.codification)?.name,
            samplePoint: showSandreCode ? samplePoint?.displayLabel : samplePoint?.name,
            _normalizedSearchString: [qualitometer?.code, qualitometer?.name, cityLabel].map(s => normalizeString(s)),
        }
    })

    const formattedParameters = campaignParameters.map(param => {
        const { parameterCode, fractionCode, supportCode, placeCode, unitCode, selectionCode } = param
        const parameter = parametersIndex[parameterCode]
        return {
            id: param.id,
            name: { value: parameter?.name.length > 60 ? parameter?.shortLabel : parameter?.name, tooltip: parameter?.name },
            unit: showSandreCode ? indexedUnits[unitCode]?.symbolWithCode : indexedUnits[unitCode]?.symbol,
            fraction: showSandreCode ? indexedFractions[fractionCode]?.labelWithCode : indexedFractions[fractionCode]?.name,
            support: showSandreCode ? indexedSupports[supportCode]?.labelWithCode : indexedSupports[supportCode]?.name,
            place: getLocalizationPicto(placeCode),
            code: parameterCode,
            selectionCode: selectionCode ?? i18n.outOfSelections,
            _normalizedSearchString: [parameter?.name, parameter?.shortLabel, parameterCode, indexedUnits[unitCode]?.symbolWithCode, indexedFractions[fractionCode]?.labelWithCode, indexedSupports[supportCode]?.labelWithCode, getLocalizationLabel(placeCode)].map(s => normalizeString(s)),
        }
    })

    const formattedSelections = campaignSelections.map(code => {
        const selection = indexedSelections[code]
        return ({
            id: code,
            code,
            name: selection?.name ?? code,
            nbParameters: selection?.parameters?.length || 0,
            _normalizedSearchString: [selection?.name, selection?.code].map(s => normalizeString(s)),
        })
    })

    return (
        <Stack spacing={1}>
            {<Card>
                <CardTitle
                    title={i18n.addException}
                    actions={[
                        {
                            icon: 'add',
                            onClick: () => setPlanningExceptions(prev => [...prev, { ...DEFAULT_EXCEPTION, planningId, parametersToDeletePlan: [...planningParameters] }]),
                        },
                    ]}
                />
            </Card>}
            {planningExceptions.length === 0 && <NoDataCard />}
            {planningExceptions.map((exception, index) => (
                <AccordionException
                    key={index}
                    exception={exception}
                    index={index}
                    setPlanningExceptions={setPlanningExceptions}
                    formattedStations={formattedStations}
                    formattedParameters={formattedParameters}
                    formattedSelections={formattedSelections}
                    planningParameters={planningParameters}
                    planningSelections={planningSelections}
                    campaignParameters={campaignParameters}
                />
            ))

            }
        </Stack>
    )
}

StepSelectExceptions.propTypes = {
    planningId: PropTypes.number,
    planningStations: PropTypes.arrayOf(PropTypes.shape({})),
    planningParameters: PropTypes.arrayOf(PropTypes.shape({})),
    planningSelections: PropTypes.arrayOf(PropTypes.string),
    planningExceptions: PropTypes.arrayOf(PropTypes.shape({})),
    setPlanningExceptions: PropTypes.func,
    campaignStations: PropTypes.arrayOf(PropTypes.shape({})),
    campaignParameters: PropTypes.arrayOf(PropTypes.shape({})),
    campaignSelections: PropTypes.arrayOf(PropTypes.string),
}

const PlanningReadMode = ({
    planning = {},
    planningExceptions = [],
    campaignStations = [],
    campaignParameters = [],
    campaign = {},
}) => {
    const {
        contributors,
        accountUser,
        qualitometers,
        parameters,
        units,
        fractions,
        supports,
        citiesIndex,
        allPoints,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        accountUser: store.AccountReducer.accountUser,
        qualitometers: store.QualityReducer.qualitometersLight,
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        citiesIndex: store.CityReducer.citiesIndex,
        allPoints: store.QualityReducer.allPoints,
    }), shallowEqual)

    const indexedQualitometers = useListIndexed(qualitometers, 'id')
    const groupedSamplePoints = useListGrouped(allPoints, 'idQualitometer')
    const indexedParameters = useListIndexed(parameters, 'code')
    const indexedUnits = useListIndexed(units, 'code')
    const indexedFractions = useListIndexed(fractions, 'code')
    const indexedSupports = useListIndexed(supports, 'code')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const formattedParametersById = useMemo(() => campaignParameters.reduce((acc, cp) => {
        const { parameterCode, fractionCode, supportCode, placeCode, unitCode } = cp
        const parameter = indexedParameters[parameterCode]
        acc[cp.id] = {
            id: cp.id,
            code: parameterCode,
            name: { value: parameter?.name?.length > 60 ? parameter?.shortLabel : parameter?.name, tooltip: parameter?.name },
            unit: showSandreCode ? indexedUnits[unitCode]?.symbolWithCode : indexedUnits[unitCode]?.symbol,
            fraction: showSandreCode ? indexedFractions[fractionCode]?.labelWithCode : indexedFractions[fractionCode]?.name,
            support: showSandreCode ? indexedSupports[supportCode]?.labelWithCode : indexedSupports[supportCode]?.name,
            place: getLocalizationPicto(placeCode),
        }
        return acc
    }, {}), [campaignParameters, indexedParameters, indexedUnits, indexedFractions, indexedSupports, showSandreCode])

    const [filter, setFilter] = useState({})

    const planningStations = hybridSearch(
        campaignStations
            .filter(cs => includes(planning.stationsPlan || [], cs.id))
            .map(cs => {
                const qualito = indexedQualitometers[cs.stationId]
                return { ...cs, _normalizedSearchString: [qualito?.name, qualito?.code].map(s => normalizeString(s)) }
            }),
        normalizeString(filter.searchValue),
    )
    const producerOptions = authorizeContributors(contributors, accountUser, planning.producer).filter(({ id }) => includes(uniq([campaign.recipientCode, campaign.samplerCode, campaign.contributorCode, ...(campaign.secondaryLabos || [])]).map(code => parseInt(code)), id))

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Grid container spacing={2} alignItems='center'>
                        <SimpleDatePicker label={i18n.startDate} value={planning.startDate} readMode />
                        <SimpleDatePicker label={i18n.endDate} value={planning.endDate} readMode />
                        <SimpleSelect label={i18n.completion} options={producerOptions} value={planning.producer} keyName='labelDisplay' readMode />
                    </Grid>
                </CardContent>
            </Card>
            <Card>
                <CardContent>
                    <SimpleFilterField filter={filter} setFilter={setFilter} />
                </CardContent>
            </Card>
            {planningStations.map(cs => {
                const applicableExceptions = planningExceptions.filter(e => includes(e.stationsPlan, cs.id))
                const toDelete = uniq(applicableExceptions.flatMap(e => e.parametersToDeletePlan))
                const toAdd = uniq(applicableExceptions.flatMap(e => e.parametersToAddPlan))
                const effectiveParamIds = uniq([...(planning.parametersPlan || []).filter(id => !includes(toDelete, id)), ...toAdd])
                const rows = effectiveParamIds.map(id => formattedParametersById[id]).filter(Boolean)
                const station = indexedQualitometers[cs.stationId]
                const town = citiesIndex[station?.townCode]?.name || ''
                const firstTitle = <Typography variant='h6'>{i18n.analysisToDoOnStation}</Typography>
                const samplePoint = groupedSamplePoints[cs.stationId]?.find(p => p.idPoint === cs.samplePoint)
                const samplePointLabel = showSandreCode ? samplePoint?.displayLabel : samplePoint?.name
                const stationName = <Typography variant='subtitle1'>{`[${station?.code}] ${town}${station?.name ? ` - ${station.name}` : ''}${samplePointLabel ? ` - ${samplePointLabel}` : ''}`}</Typography>
                return (
                    <CardTable
                        key={cs.id}
                        title={<Box>{firstTitle}{stationName}</Box>}
                        rows={rows}
                        headers={['code', 'name', 'unit', 'fraction', 'support', 'place']}
                    />
                )
            })}
        </Stack>
    )
}

PlanningReadMode.propTypes = {
    planning: PropTypes.shape({}),
    planningExceptions: PropTypes.arrayOf(PropTypes.shape({})),
    campaignStations: PropTypes.arrayOf(PropTypes.shape({})),
    campaignParameters: PropTypes.arrayOf(PropTypes.shape({})),
    campaign: PropTypes.shape({}),
}

const PopinQualityCampaignPlanning = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    campaignPlanning = {},
    campaignPlanningExceptions = [],
    isEditMode = true,
}) => {
    const { campaignStations, campaignParameters, campaign } = useContext(QualityCampaignContext)
    const [planning, setPlanning] = useState(campaignPlanning)
    const [planningExceptions, setPlanningExceptions] = useState(campaignPlanningExceptions)

    useEffect(() => {
        setPlanning(campaignPlanning)
        setPlanningExceptions(campaignPlanningExceptions)
        return () => setPlanning()
    }, [campaignPlanning, campaignPlanningExceptions, campaign])

    const updateExceptionsOnStationsChanges = () => {
        setPlanningExceptions(prevPE => compact(prevPE.map(exception => {
            const newException = {
                ...exception,
                stationsPlan: exception.stationsPlan.filter(idStation => includes(campaignStations.map(({ id })=> id), idStation)),
            }
            if (!newException.stationsPlan.length) return undefined
            return newException
        })))
    }

    const updateExceptionsOnParametersChanges = () => {
        setPlanningExceptions(prevPE => compact(prevPE.map(exception => {
            const newException = {
                ...exception,
                parametersToAddPlan: exception.parametersToAddPlan.filter(idParam => includes(campaignParameters.map(({ id })=> id), idParam)),
                parametersToDeletePlan: exception.parametersToDeletePlan.filter(idParam => includes(planning.parametersPlan, idParam)),
            }
            if (!newException.parametersToAddPlan.length && !newException.parametersToDeletePlan.length) return undefined
            return newException
        })))
    }

    if (!isEditMode) {
        return (
            <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth='lg'>
                <DefaultDialogTitle title={i18n.planning} onClose={onClose} />
                <DialogContent>
                    <PlanningReadMode
                        planning={planning}
                        planningExceptions={planningExceptions}
                        campaignStations={campaignStations}
                        campaignParameters={campaignParameters}
                        campaign={campaign}
                    />
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <StepperDialog
            steps={[
                {
                    label: i18n.qualitometersSelection,
                    constant: STEP.STATION,
                    nextAvailable: !isNil(planning.startDate) && (isNil(planning.endDate) || planning.startDate <= planning.endDate) && planning.stationsPlan.length > 0,
                    onNext: (nextStep) => {
                        if (isNil(planning.endDate)) {
                            setPlanning(prev => ({ ...prev, endDate: planning.startDate }))
                        }
                        updateExceptionsOnStationsChanges()
                        nextStep()
                    },
                },
                {
                    label: i18n.parametersSelection,
                    constant: STEP.PARAMETER,
                    onNext: (nextStep) => {
                        updateExceptionsOnParametersChanges()
                        nextStep()
                    },
                    onPrev: (prevStep) => {
                        updateExceptionsOnParametersChanges()
                        prevStep()
                    },
                },
                {
                    label: `${i18n.exceptions} (${i18n.option})`,
                    constant: STEP.EXCEPTION,
                },
            ]}
            open={isOpen}
            title={i18n.planning}
            closeDialog={onClose}
            renderSaveButton={step => step >= STEP.PARAMETER && (
                <Button
                    onClick={() => onValidate(planning, compact(planningExceptions.map(exception => {
                        const newException = {
                            ...exception,
                            stationsPlan: exception.stationsPlan.filter(idStation => includes(campaignStations.map(({ id })=> id), idStation)),
                            parametersToAddPlan: exception.parametersToAddPlan.filter(idParam => includes(campaignParameters.map(({ id })=> id), idParam)),
                            parametersToDeletePlan: exception.parametersToDeletePlan.filter(idParam => includes(planning.parametersPlan, idParam)),
                        }
                        if ((!newException.parametersToAddPlan.length && !newException.parametersToDeletePlan.length) || !newException.stationsPlan.length) return undefined
                        return newException
                    })))}
                    variant='contained'
                >
                    {i18n.validate}
                </Button>
            )}
            sx={{ minWidth: '90vw' }}
        >
            {step => (
                <>
                    {step === STEP.STATION && (
                        <StepSelectStations
                            planning={planning}
                            setPlanning={setPlanning}
                            planningExceptions={planningExceptions}
                            setPlanningExceptions={setPlanningExceptions}
                            campaignStations={campaignStations}
                        />
                    )}
                    {step === STEP.PARAMETER && (
                        <StepSelectParameters
                            planning={planning}
                            setPlanning={setPlanning}
                            planningExceptions={planningExceptions}
                            setPlanningExceptions={setPlanningExceptions}
                            campaignParameters={campaignParameters}
                        />
                    )}
                    {step === STEP.EXCEPTION && (
                        <StepSelectExceptions
                            planningId={planning.id}
                            planningStations={planning.stationsPlan}
                            planningParameters={planning.parametersPlan}
                            planningSelections={planning.selections}
                            planningExceptions={planningExceptions}
                            setPlanningExceptions={setPlanningExceptions}
                            campaignStations={campaignStations}
                            campaignParameters={campaignParameters}
                            campaignSelections={campaign.selections}
                        />
                    )}
                </>
            )}
        </StepperDialog>
    )
}

PopinQualityCampaignPlanning.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    campaign: PropTypes.shape({}),
    campaignPlanning: PropTypes.shape({}),
    campaignPlanningExceptions: PropTypes.arrayOf(PropTypes.shape({})),
    campaignStations: PropTypes.arrayOf(PropTypes.shape({})),
    campaignParameters: PropTypes.arrayOf(PropTypes.shape({})),
    isEditMode: PropTypes.bool,
}

export default PopinQualityCampaignPlanning